import { apiRequest } from '../lib/httpClient'
import { asArray, parseUtcTimestamp, pickBool, pickString } from './shared/coercion'
import { POPUP_EVENT_KEYS } from '@/features/advisory-ai/assistant/types'
import type {
  PopupEventKey,
  PopupMessage,
  PopupSetting,
  PopupTarget,
  PopupTrigger,
} from '@/features/advisory-ai/assistant/types'

/**
 * Preppy pop-ups — see PREPPY-POPUPS-SPEC.md §3 for the shipped contract.
 *
 * All response normalisation lives here and nowhere else, because the API has
 * three quirks the rest of the app should not have to know about:
 *
 *  1. **Responses are PascalCase.** No camelCase resolver is registered, so
 *     properties come back exactly as declared on the C# type.
 *  2. **Nulls are omitted rather than sent.** A pop-up with no org target has no
 *     `OrgId` key at all, so every read is defensive.
 *  3. **`CreatedAt` has no `Z` and no offset.** The column is a Postgres
 *     `timestamp` without time zone, so the value is UTC but does not say so.
 *     Parsing it naively treats it as local time and silently skews every
 *     relative label by the reader's offset — so it is parsed as UTC explicitly.
 *
 * The settings endpoint is inconsistent with the rest — its wrapper key is
 * lowercase while the entries inside are PascalCase — so both spellings are
 * accepted throughout rather than betting on which one gets normalised first.
 */

const BASE = 'advisoryai/popups'

/** Reads a property under either casing, since the API mixes the two. */
function prop(source: unknown, name: string): unknown {
  if (!source || typeof source !== 'object') return undefined
  const record = source as Record<string, unknown>
  const pascal = name.charAt(0).toUpperCase() + name.slice(1)
  return record[name] ?? record[pascal]
}

/**
 * Only the ids. The API also sends a `kind`, but the destination comes from the
 * event (see assistant/popupBehaviour.ts) and nothing reads the kind back, so it
 * is dropped here rather than carried as a field with no reader.
 */
function mapTarget(value: unknown): PopupTarget {
  const orgId = pickString(prop(value, 'orgId'))
  const callId = pickString(prop(value, 'callId'))

  return {
    ...(orgId ? { orgId } : {}),
    ...(callId ? { callId } : {}),
  }
}

const KNOWN_TRIGGERS: PopupTrigger[] = ['firsttime', 'everyuse', 'inactivity', 'milestones']

/**
 * Validated, not cast. A cast would assert an unrecognised value is one of the
 * literals, and it would then come out `undefined` from every total `Record`
 * lookup.
 */
function mapEventKey(value: unknown): PopupEventKey | null {
  const raw = pickString(value)
  return POPUP_EVENT_KEYS.includes(raw as PopupEventKey) ? (raw as PopupEventKey) : null
}

function mapTrigger(value: unknown): PopupTrigger | null {
  const raw = pickString(value)
  return KNOWN_TRIGGERS.includes(raw as PopupTrigger) ? (raw as PopupTrigger) : null
}

function mapPopup(payload: unknown): PopupMessage | null {
  const eventKey = mapEventKey(prop(payload, 'eventKey'))
  const trigger = mapTrigger(prop(payload, 'trigger'))

  /**
   * An unrecognised trigger is dropped: the stage decides how a message is
   * delivered and which settings row switches it off, and neither can be
   * honoured for a stage this client has never heard of.
   *
   * An unrecognised event key is not. It only decides an icon — delivery and
   * the settings are keyed on the trigger — so dropping one hid three live
   * messages behind an unread badge the user could not clear. It shows with the
   * default icon instead, and the warning says which key needs adding.
   */
  if (!trigger) {
    console.warn('[popups] dropped pop-up with an unrecognised trigger', {
      eventKey: prop(payload, 'eventKey'),
      trigger: prop(payload, 'trigger'),
    })
    return null
  }
  if (!eventKey) {
    console.warn('[popups] unrecognised event key, showing with the default icon', {
      eventKey: prop(payload, 'eventKey'),
    })
  }

  return {
    id: pickString(prop(payload, 'popupId')),
    eventKey: eventKey ?? 'unknown',
    trigger,
    title: pickString(prop(payload, 'title')),
    body: pickString(prop(payload, 'body')),
    createdAt: parseUtcTimestamp(prop(payload, 'createdAt')),
    unread: pickBool(prop(payload, 'unread')),
    seen: pickBool(prop(payload, 'seen')),
    playful: pickBool(prop(payload, 'playful')),
    target: mapTarget(prop(payload, 'target')),
  }
}

/**
 * No `unreadCount`: the API sends one for the whole feed, but the badge counts
 * only the pop-ups that reach the tab, so it is derived in AssistantProvider.
 * Keeping the server's figure here would mean maintaining a number nothing reads.
 */
export type PopupsResponse = {
  popups: PopupMessage[]
}

/**
 * NOTE: this GET has server-side side effects, deliberately (spec §3.1). It
 * records the AdvicePrep visit that the two `inactivity` events are computed
 * from, and it applies burst control. Do not call it speculatively, and do not
 * move the poll somewhere it fires off-route — the visit signal depends on it.
 */
export async function fetchPopups(clientId: string): Promise<PopupsResponse> {
  const payload = await apiRequest<unknown>(BASE, {
    method: 'GET',
    query: { clientId },
  })

  const rows = asArray<unknown>(prop(payload, 'popups'))
  return {
    popups: rows
      .map(mapPopup)
      .filter((p): p is PopupMessage => p !== null && Boolean(p.id)),
  }
}

/** Silently forgiving server-side: unknown or already-read ids are a no-op. */
export async function markPopupRead(popupId: string, clientId: string): Promise<void> {
  await apiRequest<void>(`${BASE}/${popupId}/read`, {
    method: 'POST',
    query: { clientId },
  })
}

/**
 * Tells the server a bubble actually appeared. Without this everything re-pops
 * on the next page load.
 */
export async function markPopupsSeen(popupIds: string[], clientId: string): Promise<void> {
  if (popupIds.length === 0) return
  await apiRequest<void>(`${BASE}/seen`, {
    method: 'POST',
    query: { clientId },
    body: { popupIds },
  })
}

/** Hidden rows are filtered out of the feed server-side, not deleted. */
export async function hidePopup(popupId: string, clientId: string): Promise<void> {
  await apiRequest<void>(`${BASE}/${popupId}/hidden`, {
    method: 'POST',
    query: { clientId },
  })
}

/** `readAll`, not `read-all` — matches `deepDive/message` in this feature. */
export async function markAllPopupsRead(clientId: string): Promise<void> {
  await apiRequest<void>(`${BASE}/readAll`, {
    method: 'POST',
    query: { clientId },
  })
}

/** Labels are client-side copy; the API only owns enabled state and channels. */
const SETTING_LABEL: Record<PopupTrigger, { name: string; delivery: string }> = {
  firsttime: { name: 'First time set up', delivery: 'In-app only, once per event' },
  everyuse: { name: 'Every use', delivery: 'In-app only — welcomes, saves, exports' },
  inactivity: { name: 'Inactivity nudges', delivery: 'In-app and email after 7 / 30 days' },
  milestones: { name: 'Activity milestones', delivery: 'In-app only — streaks and firsts' },
}

function mapSetting(payload: unknown): PopupSetting | null {
  const key = pickString(prop(payload, 'trigger')) as PopupTrigger
  const label = SETTING_LABEL[key]
  if (!label) return null

  const channels = asArray<unknown>(prop(payload, 'channels')).map(pickString).filter(Boolean)
  return {
    key,
    name: label.name,
    // Prefer what the server actually reports over the hardcoded description,
    // so a channel change does not silently contradict the label.
    delivery: channels.length
      ? channels.map((c) => (c === 'inApp' ? 'In-app' : 'Email')).join(' and ')
      : label.delivery,
    channels,
    on: pickBool(prop(payload, 'enabled'), true),
  }
}

export async function fetchPopupSettings(clientId: string): Promise<PopupSetting[]> {
  const payload = await apiRequest<unknown>(`${BASE}/settings`, {
    method: 'GET',
    query: { clientId },
  })

  return asArray<unknown>(prop(payload, 'settings'))
    .map(mapSetting)
    .filter((s): s is PopupSetting => s !== null)
}

export type UpdatePopupSettingInput = {
  clientId: string
  trigger: PopupTrigger
  enabled: boolean
  /** Optional — omitting it leaves the stored channels unchanged. */
  channels?: string[]
}

export async function updatePopupSetting(input: UpdatePopupSettingInput): Promise<void> {
  await apiRequest<void>(`${BASE}/settings`, {
    method: 'PUT',
    query: { clientId: input.clientId },
    body: {
      trigger: input.trigger,
      enabled: input.enabled,
      ...(input.channels ? { channels: input.channels } : {}),
    },
  })
}
